import React, { useState, useEffect, useCallback, useRef } from "react";
import "./index.css";
import StellenanzeigenPage from "./StellenanzeigenPage";
import PageHeader from "./PageHeader";
import PartnersPage from "./PartnersPage";
import KarriereportalPage from "./KarriereportalPage";
import PapierkorbPage from "./PapierkorbPage";
import AgentsPage from "./AgentsPage";
import ActivityPage from "./ActivityPage";
import ChatPage from "./ChatPage";
import { generationEta } from "./genEta";
import { formatCost, formatEur } from "./cost";
import {
  LayoutDashboard,
  FileText,
  Mail,
  Globe,
  FileCode,
  Users,
  UserCog,
  HardDrive,
  LogOut,
  Clipboard,
  Copy,
  Server,
  Coins,
  Activity,
  Clock,
  ChevronRight,
  Calendar,
  Filter,
  Cpu,
  Database,
  Plus,
  Trash2,
  Shield,
  ShieldCheck,
  UserX,
  UserCheck,
  Send,
  Sparkles,
  Pencil,
  Check,
  Loader,
  RotateCcw,
  Sun,
  Moon,
  Search,
  Briefcase,
  HelpCircle,
  ArrowRight,
  MessageSquare,
  CheckCircle2,
  BookOpen,
  BookMarked,
} from "lucide-react";
import HelpPage from "./HelpPage";
import LearnedRulesPage from "./LearnedRulesPage";
import UneqBausteinePage from "./UneqBausteinePage";
import EmailPage from "./EmailPage";
import { navigate as gotoPfad, usePfad } from "./router";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";

const mainNav = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "activity", label: "Aktivität", icon: Activity },
  { id: "jobs", label: "Stellenanzeigen", icon: FileText },
  { id: "emails", label: "E-Mails", icon: Mail },
  { id: "careers", label: "Karriereportal", icon: Globe },
  { id: "landings", label: "Landingpages", icon: FileCode },
  { id: "chat", label: "Text-Chat", icon: MessageSquare },
];

const aiNav = [
  { id: "agents", label: "KI-Agenten", icon: Sparkles },
  { id: "learned-rules", label: "KI-Optimierungen", icon: BookOpen },
];

const settingsNav = [
  { id: "partners", label: "Partner", icon: Users },
  { id: "users", label: "Benutzer", icon: UserCog },
  { id: "uneq-bausteine", label: "UNEQ-Werte & Bausteine", icon: BookMarked },
  { id: "papierkorb", label: "Papierkorb", icon: Trash2 },
  { id: "backups", label: "Backups", icon: HardDrive },
  { id: "help", label: "Anleitung & Hilfe", icon: HelpCircle },
];

const PAGE_TITLES = {
  dashboard: "Dashboard",
  jobs: "Stellenanzeigen",
  emails: "E-Mails",
  careers: "Karriereportal",
  landings: "Landingpages",
  chat: "Text-Chat",
  agents: "KI-Agenten",
  partners: "Partner",
  activity: "Aktivität",
  users: "Benutzer",
  "learned-rules": "KI-Optimierungen",
  "uneq-bausteine": "UNEQ-Werte & Bausteine",
  backups: "Backups",
  papierkorb: "Papierkorb",
  help: "Anleitung & Hilfe",
};

const PAGE_EYEBROWS = {
  dashboard: "Übersicht",
  jobs: "Texte",
  emails: "Texte",
  careers: "Texte",
  landings: "Texte",
  chat: "Texte",
  agents: "Künstliche Intelligenz",
  partners: "Verwaltung",
  activity: "Verwaltung",
  users: "Verwaltung",
  "learned-rules": "Künstliche Intelligenz",
  "uneq-bausteine": "UNEQ",
  backups: "Verwaltung",
  papierkorb: "Verwaltung",
  help: "Verwaltung",
};

const contentCards = [
  { icon: FileText, title: "Stellenanzeigen", desc: "Stellenanzeigen, die Top-Talente erreichen." },
  { icon: Mail, title: "E-Mails", desc: "E-Mail-Kampagnen mit KI-gestütztem Copywriting." },
  { icon: Globe, title: "Karriereportal", desc: "Karriereseiten, die zur Marke passen." },
  { icon: FileCode, title: "Landingpages", desc: "Landingpages mit conversionstarkem Copy." },
];


function formatUptime(seconds) {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function formatMonthLabel(monthStr) {
  const [y, m] = monthStr.split("-");
  const months = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];
  return `${months[parseInt(m, 10) - 1]} ${y}`;
}

const DASH_STAGE_LABELS = {
  pending: "In Warteschlange", agent1: "Schreiben", "agents2-8": "Prüfung",
  agent7: "Überarbeitung", "final-check": "Schlussprüfung", formatting: "Formatierung", done: "Fertig",
};
function stageLabelShort(stage) {
  if (!stage) return "Läuft …";
  if (DASH_STAGE_LABELS[stage]) return DASH_STAGE_LABELS[stage];
  const m = String(stage).match(/^(review|rewrite)-(\d+)$/);
  if (m) return `${m[1] === "review" ? "Prüfung" : "Überarbeitung"} · Runde ${m[2]}`;
  return stage;
}

const TAB_NAV = [
  { id: "jobs", label: "Stellenanzeigen", icon: FileText },
  { id: "emails", label: "E-Mails", icon: Mail },
  { id: "careers", label: "Karriereportal", icon: Globe },
  { id: "landings", label: "Landingpages", icon: FileCode },
];

// Map recent project types to icons (for now all are Stellenanzeigen)
const TYPE_ICONS = {
  stellenanzeige: FileText,
  email: Mail,
  karriereportal: Globe,
  landingpage: FileCode,
};

function GaugeBar({ percent, color }) {
  return (
    <div className="gauge-bar">
      <div className="gauge-bar-fill" style={{ width: `${Math.min(100, percent)}%`, background: color || "#FBB040" }} />
    </div>
  );
}

function Dashboard({ onNavigate }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [inboxNew, setInboxNew] = useState(0);
  const [act, setAct] = useState({ approvals: 0, openComments: 0, views: 0 });

  useEffect(() => {
    fetch("/api/dashboard/stats")
      .then((r) => r.json())
      .then((data) => {
        setStats(data);
        setSelectedMonth(data.currentMonth);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  // Laufende Generierungen (Polling)
  useEffect(() => {
    const load = () => fetch("/api/generation-jobs", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setJobs(Array.isArray(d) ? d : []))
      .catch(() => {});
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, []);

  // Neue Einreichungen (ungesehen)
  useEffect(() => {
    const load = () => fetch("/api/jotform/inbox/unseen-count", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : { count: 0 }))
      .then((d) => setInboxNew(d.count || 0))
      .catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  // Partner-Aktivität (aggregiert)
  useEffect(() => {
    fetch("/api/share-activity", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => {
        const arr = Array.isArray(d) ? d : [];
        setAct({
          approvals: arr.filter((x) => x.approved_at).length,
          openComments: arr.reduce((s, x) => s + (x.comments_open || 0), 0),
          views: arr.reduce((s, x) => s + (x.views || 0), 0),
        });
      })
      .catch(() => {});
  }, []);

  const isAllTime = selectedMonth === "__all__";
  const activeMonth = isAllTime ? null : (selectedMonth || (stats && stats.currentMonth));

  // Kosten (exakt, aus gespeicherten cost_eur pro Anzeige) für Monat oder gesamt
  const getCostForMonth = (month) => {
    if (!stats) return 0;
    const m = stats.monthly.find((x) => x.month === month);
    return m ? Number(m.cost_eur) || 0 : 0;
  };

  const allTimeCost = stats ? Number(stats.totals.cost_eur) || 0 : 0;
  const displayCost = isAllTime ? allTimeCost : getCostForMonth(activeMonth);

  // Cost categories (currently only Stellenanzeigen has real data)
  const costCategories = [
    { label: "Stellenanzeigen", icon: FileText, cost: displayCost },
    { label: "E-Mails", icon: Mail, cost: 0 },
    { label: "Karriereportal", icon: Globe, cost: 0 },
    { label: "Landingpages", icon: FileCode, cost: 0 },
  ];
  const totalCost = costCategories.reduce((s, c) => s + c.cost, 0);

  return (
    <>
      <PageHeader
        eyebrow="Übersicht"
        title="Dashboard"
        subtitle="Aktuelle Projekte, Kosten und Schnellzugriff auf alle Bereiche."
      />

      {/* Tab Navigation */}
      <div className="dash-tabs">
        {TAB_NAV.map((tab) => {
          const Icon = tab.icon;
          return (
            <button key={tab.id} className="dash-tab" onClick={() => onNavigate(tab.id)}>
              <Icon size={18} />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Status-Kacheln */}
      <div className="dash-stat-row">
        <button className="dash-stat card" onClick={() => onNavigate("activity")}>
          <Clipboard size={18} />
          <div className="dash-stat-value">{inboxNew}</div>
          <div className="dash-stat-label">Neue Einreichungen</div>
        </button>
        <button className="dash-stat card" onClick={() => onNavigate("activity")}>
          <CheckCircle2 size={18} />
          <div className="dash-stat-value">{act.approvals}</div>
          <div className="dash-stat-label">Freigaben</div>
        </button>
        <button className="dash-stat card" onClick={() => onNavigate("activity")}>
          <MessageSquare size={18} />
          <div className="dash-stat-value">{act.openComments}</div>
          <div className="dash-stat-label">Offene Kommentare</div>
        </button>
        <button className="dash-stat card" onClick={() => onNavigate("activity")}>
          <Activity size={18} />
          <div className="dash-stat-value">{act.views}</div>
          <div className="dash-stat-label">Aufrufe</div>
        </button>
      </div>

      {/* Laufende Generierungen */}
      {jobs.length > 0 && (
        <div className="dash-section">
          <h3 className="dash-section-title"><Sparkles size={16} /> Live-Generierung <span className="dash-live-count">{jobs.length}</span></h3>
          <div className="dash-live-list">
            {jobs.map((j) => (
              <div className="dash-live-row card" key={j.id} onClick={() => onNavigate("agents")}>
                <Loader size={16} className="icon-spin" />
                <div className="dash-live-info">
                  <h4>{j.stellentitel || "Ohne Titel"}</h4>
                  <span className="dash-recent-meta">{j.unternehmen}</span>
                </div>
                <span className="dash-live-stage">
                  {stageLabelShort(j.stage)}
                  {generationEta(j.created_at, j.avg_duration_sec).text ? ` · ${generationEta(j.created_at, j.avg_duration_sec).text}` : ""}
                </span>
                <ChevronRight size={16} />
              </div>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading-indicator"><div className="spinner" /><span>Lade Dashboard …</span></div>
      ) : stats && (
        <>
          {/* Cost + Storage side by side */}
          <div className="dash-two-col">
            {/* Cost Overview */}
            <div className="dash-section">
              <div className="dash-section-header">
                <h3 className="dash-section-title"><Coins size={16} /> Kostenübersicht</h3>
                <div className="dash-month-filter">
                  <Filter size={14} />
                  <select
                    value={isAllTime ? "__all__" : activeMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    className="dash-month-select"
                  >
                    {stats.monthly.map((m) => (
                      <option key={m.month} value={m.month}>
                        {formatMonthLabel(m.month)}{m.month === stats.currentMonth ? " (aktuell)" : ""}
                      </option>
                    ))}
                    <option value="__all__">Gesamtkosten (alle Monate)</option>
                  </select>
                </div>
              </div>

              <div className="dash-cost-box card">
                <div className="dash-cost-categories">
                  {costCategories.map((cat) => {
                    const CatIcon = cat.icon;
                    return (
                      <div className="dash-cost-cat-row" key={cat.label}>
                        <div className="dash-cost-cat-label">
                          <CatIcon size={16} />
                          <span>{cat.label}</span>
                        </div>
                        <div className="dash-cost-cat-value">{formatEur(cat.cost)}</div>
                      </div>
                    );
                  })}
                </div>
                <div className="dash-cost-divider" />
                <div className="dash-cost-cat-row dash-cost-cat-total">
                  <div className="dash-cost-cat-label">
                    <Coins size={16} />
                    <span>Gesamtkosten</span>
                  </div>
                  <div className="dash-cost-cat-value">{formatEur(totalCost)}</div>
                </div>
              </div>
            </div>

            {/* Storage Overview */}
            <div className="dash-section">
              <h3 className="dash-section-title"><HardDrive size={16} /> Speicher</h3>
              <div className="dash-storage-box card">
                <div className="dash-storage-header">
                  <div className="dash-storage-used">{(stats.server.diskUsedMB / 1024).toFixed(1)} GB</div>
                  <div className="dash-storage-total">von {(stats.server.diskTotalMB / 1024).toFixed(1)} GB belegt</div>
                </div>
                <GaugeBar percent={stats.server.diskPercent} color={stats.server.diskPercent > 80 ? "#e74c3c" : stats.server.diskPercent > 50 ? "#f39c12" : "#FBB040"} />
                <div className="dash-storage-percent">{stats.server.diskPercent}% belegt</div>

                {stats.server.diskBreakdown && stats.server.diskBreakdown.length > 0 && (
                  <div className="dash-storage-breakdown">
                    {stats.server.diskBreakdown.map((item, i) => (
                      <div className="dash-storage-row" key={i}>
                        <span className="dash-storage-label">{item.label}</span>
                        <span className="dash-storage-size">{item.mb >= 1024 ? `${(item.mb / 1024).toFixed(1)} GB` : `${item.mb} MB`}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Recent Projects */}
          <div className="dash-section">
            <h3 className="dash-section-title"><Clock size={16} /> Letzte Projekte</h3>
            <div className="dash-recent-list">
              {stats.recent.map((r) => {
                const projectType = r.type || "stellenanzeige";
                const TypeIcon = TYPE_ICONS[projectType] || FileText;
                return (
                  <div className="dash-recent-row card" key={r.id} onClick={() => {
                    gotoPfad(`/jobs/${r.partner_id}/${r.id}`);
                    onNavigate("jobs");
                  }}>
                    <div className="dash-recent-icon">
                      <TypeIcon size={18} />
                    </div>
                    <div className="dash-recent-info">
                      <h4>{r.stellentitel}</h4>
                      <span className="dash-recent-meta">
                        {r.unternehmen} · {r.farbtyp} · {new Date(r.created_at).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })}
                      </span>
                    </div>
                    <div className="dash-recent-cost">{r.cost_eur != null ? formatEur(r.cost_eur) : formatCost(r.total_tokens)}</div>
                    <ChevronRight size={16} />
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </>
  );
}


function GoogleG() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"
      />
    </svg>
  );
}

function LoginPage({ onLogin }) {
  const googleBtnRef = useRef(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) {
      setError("Google-Login ist nicht konfiguriert (Client-ID fehlt). Bitte Administrator informieren.");
      return;
    }

    let cancelled = false;
    let done = false;
    let waited = 0;
    let pollTimer = null;

    const isReady = () =>
      window.google && window.google.accounts && window.google.accounts.id;

    const setupGoogle = () => {
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: async (response) => {
          setLoading(true);
          setError("");
          try {
            const res = await fetch("/api/auth/google", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              body: JSON.stringify({ credential: response.credential }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Login fehlgeschlagen");
            onLogin(data.user);
          } catch (err) {
            setError(err.message);
            setLoading(false);
          }
        },
      });
      window.google.accounts.id.renderButton(googleBtnRef.current, {
        type: "standard",
        theme: "filled_black",
        size: "large",
        text: "signin_with",
        shape: "rectangular",
        width: 260,
      });
    };

    // Das GSI-Script lädt async/defer – beim ersten Render ist window.google
    // oft noch nicht da. Warten statt still abzubrechen, sonst bleibt der
    // (unsichtbare) Google-Button leer und der Login ist nicht anklickbar.
    const tryInit = () => {
      if (cancelled || done) return;
      if (isReady()) {
        done = true;
        if (pollTimer) clearInterval(pollTimer);
        try {
          setupGoogle();
        } catch (err) {
          setError("Google-Anmeldung konnte nicht initialisiert werden.");
        }
        return;
      }
      waited += 200;
      if (waited >= 10000) {
        done = true;
        if (pollTimer) clearInterval(pollTimer);
        setError("Google-Anmeldedienst konnte nicht geladen werden. Bitte Seite neu laden oder Browser-Erweiterungen (Ad-/Script-Blocker) prüfen.");
      }
    };

    pollTimer = setInterval(tryInit, 200);
    tryInit();

    return () => {
      cancelled = true;
      if (pollTimer) clearInterval(pollTimer);
    };
  }, [onLogin]);

  return (
    <div className="login-page">
      <img src="/uneq-wordmark-light.svg" alt="UNEQ" className="login-wordmark" />
      <div className="login-content">
        <span className="login-eyebrow">Internes Tool</span>
        <h1 className="login-headline">Copywriting.</h1>
        <span className="login-rule" />
        <p className="login-desc">
          KI-gestützte Stellenanzeigen, Karriere- und Landingpage-Texte für unsere
          Partner. Melde dich mit deinem UNEQ-Konto an, um zu starten.
        </p>
        {error && <div className="login-error">{error}</div>}
        {/* UNEQ-Orange-Button als Optik; der echte (transparente) Google-Button
            liegt darüber und übernimmt den Login (ID-Token → credential). */}
        <div className="login-google">
          <button type="button" className="login-btn" aria-hidden="true" tabIndex={-1} disabled={loading}>
            {loading ? (
              <>
                <span className="login-btn-spinner" /> Anmeldung läuft …
              </>
            ) : (
              <>
                <GoogleG />
                <span>Mit Google anmelden</span>
                <ArrowRight size={17} />
              </>
            )}
          </button>
          <div
            ref={googleBtnRef}
            className="login-google-real"
            style={loading ? { pointerEvents: "none" } : undefined}
            aria-label="Mit Google anmelden"
          />
        </div>
        <p className="login-hint">Nur @uneq.de E-Mail-Adressen sind zugelassen.</p>
      </div>
      <img
        src="/make-it-unequaled-white.svg"
        alt="Make it UNEQUALED."
        className="login-signoff"
      />
    </div>
  );
}

function UsersPage({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState("");
  const [newName, setNewName] = useState("");
  const [error, setError] = useState("");

  const loadUsers = useCallback(() => {
    fetch("/api/users", { credentials: "include" })
      .then((r) => r.json())
      .then((data) => { if (Array.isArray(data)) setUsers(data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleAdd = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const res = await fetch("/api/users", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: newEmail, name: newName }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setNewEmail("");
      setNewName("");
      loadUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const toggleActive = async (user) => {
    await fetch(`/api/users/${user.id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ active: !user.active }),
    });
    loadUsers();
  };

  const toggleRole = async (user) => {
    const newRole = user.role === "admin" ? "user" : "admin";
    await fetch(`/api/users/${user.id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ role: newRole }),
    });
    loadUsers();
  };

  const deleteUser = async (user) => {
    if (!window.confirm(`${user.email} wirklich löschen?`)) return;
    await fetch(`/api/users/${user.id}`, { method: "DELETE", credentials: "include" });
    loadUsers();
  };

  if (currentUser.role !== "admin") {
    return (
      <>
        <PageHeader eyebrow="Verwaltung" title="Benutzer" />
        <div className="empty-card card">
          <Shield size={32} strokeWidth={1.5} />
          <p style={{ marginTop: 12 }}>Nur Administratoren können Benutzer verwalten.</p>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        eyebrow="Verwaltung"
        title="Benutzer"
        subtitle="Verwalte, wer sich bei UNEQ Copywriting anmelden darf."
      />

      {/* Add user form */}
      <form className="users-add-form card" onSubmit={handleAdd}>
        <div className="users-add-fields">
          <input
            type="email"
            placeholder="E-Mail (@uneq.de)"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            required
          />
          <input
            type="text"
            placeholder="Name (optional)"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
          <button type="submit" className="btn-primary"><Plus size={16} /> Hinzufügen</button>
        </div>
        {error && <div className="users-error">{error}</div>}
      </form>

      {/* User list */}
      {loading ? (
        <div className="loading-indicator"><div className="spinner" /><span>Lade Benutzer …</span></div>
      ) : (
        <div className="users-list">
          {users.map((u) => (
            <div className={`users-row card ${!u.active ? "users-row-inactive" : ""}`} key={u.id}>
              <div className="users-avatar">
                {u.picture ? <img src={u.picture} alt="" /> : <UserCog size={20} />}
              </div>
              <div className="users-info">
                <div className="users-name">
                  {u.name || u.email.split("@")[0]}
                  {u.role === "admin" && <span className="users-badge">Admin</span>}
                  {!u.active && <span className="users-badge users-badge-inactive">Deaktiviert</span>}
                </div>
                <div className="users-email">{u.email}</div>
                <div className="users-meta">
                  {u.last_login
                    ? `Letzter Login: ${new Date(u.last_login).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}`
                    : "Noch nie eingeloggt"}
                </div>
              </div>
              <div className="users-actions">
                <button className="users-action-btn" onClick={() => toggleRole(u)} data-tooltip={u.role === "admin" ? "Admin entfernen" : "Zum Admin machen"}>
                  {u.role === "admin" ? <ShieldCheck size={16} /> : <Shield size={16} />}
                </button>
                <button className="users-action-btn" onClick={() => toggleActive(u)} data-tooltip={u.active ? "Deaktivieren" : "Aktivieren"}>
                  {u.active ? <UserX size={16} /> : <UserCheck size={16} />}
                </button>
                {u.id !== currentUser.id && (
                  <button className="users-action-btn users-action-delete" onClick={() => deleteUser(u)} data-tooltip="Löschen">
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function PlaceholderPage({ title, eyebrow }) {
  return (
    <>
      <PageHeader
        eyebrow={eyebrow || "UNEQ"}
        title={title}
        subtitle="Dieser Bereich wird bald verfügbar sein."
      />
      <div className="empty-card card">
        <Clipboard size={32} strokeWidth={1.5} />
        <p style={{ marginTop: 12 }}>Noch keine Inhalte vorhanden.</p>
      </div>
    </>
  );
}

function getNavFromPath() {
  const path = window.location.pathname.replace(/^\/+/, "") || "dashboard";
  const firstSegment = path.split("/")[0];
  // aiNav gehoert dazu: sonst fallen /agents und /learned-rules aufs Dashboard
  // zurueck, weil ihre Ids in der Liste fehlen.
  const allIds = [...mainNav, ...aiNav, ...settingsNav].map((n) => n.id);
  return allIds.includes(firstSegment) ? firstSegment : "dashboard";
}

class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 32, color: "#e74c3c" }}>
          <h3>Fehler</h3>
          <pre style={{ whiteSpace: "pre-wrap", fontSize: 13 }}>{this.state.error.message}{"\n"}{this.state.error.stack}</pre>
          <button onClick={() => { this.setState({ error: null }); gotoPfad("/jobs"); }}>Zurück</button>
        </div>
      );
    }
    return this.props.children;
  }
}

function getInitialTheme() {
  try {
    const t = localStorage.getItem("uneq-theme");
    if (t === "dark" || t === "light") return t;
  } catch (e) {}
  return "dark";
}

function SidebarSearch({ onNavigate }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState({ partners: [], anzeigen: [] });
  const [highlight, setHighlight] = useState(0);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);

  // Debounced search
  useEffect(() => {
    if (!query || query.trim().length < 2) {
      setResults({ partners: [], anzeigen: [] });
      return;
    }
    setLoading(true);
    const handle = setTimeout(() => {
      fetch(`/api/search?q=${encodeURIComponent(query.trim())}`, { credentials: "include" })
        .then((r) => r.json())
        .then((data) => {
          if (data && !data.error) {
            setResults({ partners: data.partners || [], anzeigen: data.anzeigen || [] });
            setHighlight(0);
          }
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    }, 180);
    return () => clearTimeout(handle);
  }, [query]);

  // Close on outside click
  useEffect(() => {
    function onClick(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const flatHits = [
    ...results.partners.map((p) => ({ kind: "partner", id: p.partner_id, label: p.unternehmen, sub: "Partner", archived: p.archived })),
    ...results.anzeigen.map((a) => ({ kind: "anzeige", id: a.id, partner_id: a.partner_id, label: a.stellentitel || "Ohne Titel", sub: `${a.unternehmen}${a.standort ? " · " + a.standort : ""}` })),
  ];

  function go(hit) {
    if (hit.kind === "partner") {
      gotoPfad(`/jobs/${hit.id}`);
    } else {
      gotoPfad(`/jobs/${hit.partner_id}/${hit.id}`);
    }
    onNavigate("jobs");
    setQuery("");
    setOpen(false);
  }

  function onKeyDown(e) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, flatHits.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      const hit = flatHits[highlight];
      if (hit) { e.preventDefault(); go(hit); }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  return (
    <div ref={containerRef} className="sidebar-search">
      <div className="sidebar-search-input-wrap">
        <Search size={14} />
        <input
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Suchen …"
          className="sidebar-search-input"
        />
      </div>
      {open && query.trim().length >= 2 && (
        <div className="sidebar-search-dropdown">
          {loading && flatHits.length === 0 ? (
            <div className="sidebar-search-empty">Suche …</div>
          ) : flatHits.length === 0 ? (
            <div className="sidebar-search-empty">Keine Treffer</div>
          ) : (
            <>
              {results.anzeigen.length > 0 && (
                <div className="sidebar-search-group">
                  <div className="sidebar-search-group-label">Stellenanzeigen</div>
                  {results.anzeigen.map((a, i) => {
                    const idx = i;
                    const flatIdx = results.partners.length + idx;
                    return (
                      <button
                        key={`a-${a.id}`}
                        className={`sidebar-search-hit ${highlight === flatIdx ? "is-active" : ""}`}
                        onMouseEnter={() => setHighlight(flatIdx)}
                        onClick={() => go({ kind: "anzeige", id: a.id, partner_id: a.partner_id })}
                      >
                        <FileText size={13} />
                        <div className="sidebar-search-hit-text">
                          <span className="sidebar-search-hit-label">{a.stellentitel || "Ohne Titel"}</span>
                          <span className="sidebar-search-hit-sub">{a.unternehmen}{a.standort ? " · " + a.standort : ""}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
              {results.partners.length > 0 && (
                <div className="sidebar-search-group">
                  <div className="sidebar-search-group-label">Partner</div>
                  {results.partners.map((p, i) => (
                    <button
                      key={`p-${p.partner_id}`}
                      className={`sidebar-search-hit ${highlight === i ? "is-active" : ""}`}
                      onMouseEnter={() => setHighlight(i)}
                      onClick={() => go({ kind: "partner", id: p.partner_id })}
                    >
                      <Briefcase size={13} />
                      <div className="sidebar-search-hit-text">
                        <span className="sidebar-search-hit-label">
                          {p.unternehmen}
                          {p.archived && <span className="sidebar-search-hit-tag">archiv</span>}
                        </span>
                        <span className="sidebar-search-hit-sub">Partner</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [activeNav, setActiveNav] = useState(getNavFromPath);
  const [theme, setTheme] = useState(getInitialTheme);
  const [inboxNewCount, setInboxNewCount] = useState(0);
  const [ruleProposalCount, setRuleProposalCount] = useState(0);

  useEffect(() => {
    if (theme === "light") {
      document.documentElement.setAttribute("data-theme", "light");
    } else {
      document.documentElement.removeAttribute("data-theme");
    }
    try { localStorage.setItem("uneq-theme", theme); } catch (e) {}
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setTheme((t) => (t === "dark" ? "light" : "dark"));
  }, []);

  // Check existing session on mount
  useEffect(() => {
    fetch("/api/auth/me", { credentials: "include" })
      .then((r) => r.json())
      .then((data) => { if (data.user) setUser(data.user); })
      .catch(() => {})
      .finally(() => setAuthChecked(true));
  }, []);

  // Favicon nach Rolle: Admins sehen die orange Kachel, alle anderen die graue.
  const istAdmin = !!user && user.role === "admin";
  useEffect(() => {
    const link = document.querySelector('link[rel="icon"]');
    if (link) link.href = istAdmin ? "/favicon-admin.svg" : "/favicon.svg";
  }, [istAdmin]);

  const pfad = usePfad();

  const markInboxSeen = useCallback(() => {
    setInboxNewCount(0);
    fetch("/api/jotform/inbox/seen", { method: "POST", credentials: "include" }).catch(() => {});
  }, []);

  const navigate = useCallback((id) => {
    gotoPfad(`/${id}`);
    if (id === "activity") markInboxSeen();
  }, [markInboxSeen]);

  // Der Tab ergibt sich aus der Adresse - egal ob sie ueber navigate(),
  // einen Link oder Zurueck/Vorwaerts im Browser gesetzt wurde.
  useEffect(() => {
    setActiveNav(getNavFromPath());
  }, [pfad]);

  // "Neu"-Badge: ungesehene Jotform-Einreichungen pollen (nur eingeloggt).
  useEffect(() => {
    if (!user) return undefined;
    let active = true;
    const load = () =>
      fetch("/api/jotform/inbox/unseen-count", { credentials: "include" })
        .then((r) => (r.ok ? r.json() : { count: 0 }))
        .then((d) => { if (active) setInboxNewCount(d.count || 0); })
        .catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => { active = false; clearInterval(t); };
  }, [user]);

  // Badge: offene Regel-Vorschläge zählen (pollt + aktualisiert beim Navigieren,
  // damit das Freigeben/Ablehnen auf der Seite sich beim Verlassen widerspiegelt).
  useEffect(() => {
    if (!user) return undefined;
    let active = true;
    const load = () =>
      fetch("/api/redaktions-regeln?status=vorgeschlagen", { credentials: "include" })
        .then((r) => (r.ok ? r.json() : { regeln: [] }))
        .then((d) => { if (active) setRuleProposalCount(Array.isArray(d.regeln) ? d.regeln.length : 0); })
        .catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => { active = false; clearInterval(t); };
  }, [user, activeNav]);

  const handleLogout = async () => {
    await fetch("/api/auth/logout", { method: "POST", credentials: "include" });
    setUser(null);
    gotoPfad("/");
  };

  const openPartner = useCallback((p) => {
    gotoPfad(`/partners/${p.partner_id}`);
  }, []);

  // Show nothing while checking session
  if (!authChecked) {
    return (
      <div className="login-loading">
        <div className="loading-indicator"><div className="spinner" /><span>Laden …</span></div>
      </div>
    );
  }

  // Show login if not authenticated
  if (!user) {
    return <LoginPage onLogin={(u) => setUser(u)} />;
  }

  const renderPage = () => {
    switch (activeNav) {
      case "emails": return <EmailPage />;
      case "jobs": return <StellenanzeigenPage />;
      case "backups": return <StellenanzeigenPage key="backups-page" initialView="backups" />;
      case "dashboard": return <Dashboard onNavigate={navigate} />;
      case "users": return <UsersPage currentUser={user} />;
      case "partners": return <PartnersPage onOpenPartner={openPartner} isAdmin={!!user && user.role === "admin"} />;
      case "careers": return <KarriereportalPage />;
      case "agents": return <AgentsPage />;
      case "chat": return <ChatPage onNavigate={navigate} />;
      case "activity": return <ActivityPage onNavigate={navigate} />;
      case "learned-rules": return <LearnedRulesPage />;
      case "uneq-bausteine": return <UneqBausteinePage />;
      case "papierkorb": return <PapierkorbPage />;
      case "help": return <HelpPage />;
      default: return <PlaceholderPage title={PAGE_TITLES[activeNav] || activeNav} eyebrow={PAGE_EYEBROWS[activeNav]} />;
    }
  };

  const renderNavItem = (item) => {
    const Icon = item.icon;
    const showNew = item.id === "activity" && inboxNewCount > 0 && activeNav !== "activity";
    const showRuleCount = item.id === "learned-rules" && ruleProposalCount > 0;
    return (
      <li key={item.id}>
        <a
          href={`/${item.id}`}
          className={activeNav === item.id ? "active" : ""}
          onClick={(e) => { e.preventDefault(); navigate(item.id); }}
        >
          <Icon size={18} />
          <span>{item.label}</span>
          {showNew && <span className="nav-badge-new">Neu</span>}
          {showRuleCount && <span className="nav-badge-count" title={`${ruleProposalCount} offene Vorschläge`}>{ruleProposalCount}</span>}
        </a>
      </li>
    );
  };

  return (
    <div className="layout">
      <div className="uneq-q-bg" aria-hidden="true" />
      <aside className="sidebar">
        <div className="sidebar-brand">
          <div className="sidebar-brand-row">
            <img
              src={theme === "light" ? "/uneq-wordmark-dark.svg" : "/uneq-wordmark-light.svg"}
              alt="UNEQ"
              className="sidebar-wordmark"
            />
            <span className="sidebar-product">Copywriting</span>
          </div>
          <span className="sidebar-brand-rule" />
        </div>

        <SidebarSearch onNavigate={navigate} />

        <div className="sidebar-section-label">Texte</div>
        <ul className="sidebar-nav">
          {mainNav.map(renderNavItem)}
        </ul>

        <div className="sidebar-section-label">Künstliche Intelligenz</div>
        <ul className="sidebar-nav">
          {aiNav.map(renderNavItem)}
        </ul>

        <div className="sidebar-section-label">Einstellungen</div>
        <ul className="sidebar-nav">
          {settingsNav.map(renderNavItem)}
        </ul>

        <div className="sidebar-bottom">
          {user && (
            <div className="sidebar-user-info">
              {user.picture && <img src={user.picture} alt="" className="sidebar-user-avatar" />}
              <div className="sidebar-user-details">
                <span className="sidebar-user-name">{user.name}</span>
                <span className="sidebar-user-email">{user.email}</span>
              </div>
            </div>
          )}
          <button type="button" className="sidebar-theme-toggle" onClick={toggleTheme}
            title={theme === "dark" ? "Light Mode" : "Dark Mode"}>
            {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
            <span>{theme === "dark" ? "Light Mode" : "Dark Mode"}</span>
          </button>
          <a href="#logout" className="sidebar-logout" onClick={(e) => { e.preventDefault(); handleLogout(); }}>
            <LogOut size={16} />
            <span>Abmelden</span>
          </a>
          <img
            src={theme === "light" ? "/make-it-unequaled-ground.svg" : "/make-it-unequaled-white.svg"}
            alt="Make it UNEQUALED."
            className="sidebar-signoff"
          />
        </div>
      </aside>

      <div className="main">
        <header className="header header--slim">
          <span className="header-crumb">{PAGE_EYEBROWS[activeNav] || "UNEQ"} <span className="header-crumb-sep">/</span> <strong>{PAGE_TITLES[activeNav] || ""}</strong></span>
          <div className="header-user">
            <span className="header-username">{user.name || user.email}</span>
            <span className="header-role">{user.role === "admin" ? "Administrator" : "Benutzer"}</span>
          </div>
        </header>

        <main className="content"><ErrorBoundary>{renderPage()}</ErrorBoundary></main>
      </div>
    </div>
  );
}

export default App;
